import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import revlvLogo from '../assets/revlv-logo.png';
import { useAuth } from '../auth/AuthContext';
import { ChangeCounts, type PermissionChange } from '../auth/PermissionChangeWatcher';
import { Button } from '../components/Button';
import { Wordmark } from '../components/Wordmark';

/**
 * Where a reader lands when a permission change touches the page they were on.
 *
 * Outside the dashboard layout on purpose: the sidebar and the page were drawn from the
 * permissions the account signed in with, and the change just made them wrong. The one thing
 * offered is signing out, which is what brings the screen back into line with the server.
 *
 * **A courtesy, not the control** — the server refuses anything the account no longer holds
 * whether or not anybody lands here.
 */
export function PermissionsChanged() {
  const { logout } = useAuth();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);

  const change = location.state as PermissionChange | null;

  // Arriving with no change to report — a bookmark, a reload, the page after signing in again.
  if (!change) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-panel bg-brand-500 shadow-raised">
            <ShieldAlert className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-slate-800"><Wordmark /></h1>
        </div>

        <div className="flex flex-col gap-5 rounded-panel border border-slate-200 bg-white p-6 shadow-panel">
          <div className="flex flex-col gap-2 text-center">
            <p className="text-sm font-medium text-slate-800">Your permissions have changed</p>
            <p className="text-xs text-slate-500">
              An administrator changed your role or what it grants, and the page you were on is
              affected. Sign out and sign in again to continue.
            </p>
            <ChangeCounts change={change} />
          </div>

          <Button
            id="permissions-changed-page-sign-out"
            variant="primary"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void logout();
            }}
          >
            Sign out
          </Button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <img src={revlvLogo} alt="" aria-hidden="true" className="h-5 w-5 shrink-0" />
          <span className="text-xs text-slate-600">Revlv Solutions Inc.</span>
        </div>
      </div>
    </main>
  );
}
